import { ArrowLeft, Eye, AlertTriangle, Search } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Head } from "vite-react-ssg";
import { Card, CardContent } from "@/components/ui/card";
import NavigationButtons from "@/components/NavigationButtons";
import RelatedLinks from "@/components/RelatedLinks";
import LastUpdated from "@/components/LastUpdated";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const canonical = "https://deepseek.ai/blog/deepseek-vision-api-guide";
const PUBLISHED = "2026-08-23T09:00:00+00:00";

const limits = [
  { limit: "Supported image formats", value: "JPEG, PNG, GIF, WebP (detected from file content, not the file name or MIME type)" },
  { limit: "External URL length", value: "8,192 characters" },
  { limit: "Request body size", value: "48 MiB" },
  { limit: "Max single image (base64 or external URL)", value: "32 MiB" },
  { limit: "Max single image (Files API file_id)", value: "64 MiB" },
  { limit: "Max images per request", value: "600" },
  { limit: "Max total image size per request", value: "64 MiB without file_id images; up to 200 MiB including file_id images" },
  { limit: "Max image dimension", value: "8,192 px per side — drops to 4,096 px per side when a request contains 15 or more images" },
  { limit: "External image download timeout", value: "60 seconds" },
];

const detailLevels = [
  { value: "low", behavior: "The image is downscaled to 512×512 before inference. Faster and cheaper when fine visual detail does not matter." },
  { value: "high", behavior: "Keeps the original image. Provided for OpenAI compatibility; equivalent to original." },
  { value: "original", behavior: "Keeps the original image." },
  { value: "auto", behavior: "Automatic selection. Currently equivalent to original." },
];

const faqs = [
  {
    q: "Which DeepSeek model supports image input?",
    a: "Only deepseek-v4-flash-vision-exp accepts images. Every other DeepSeek model returns a 400 error with the message 'This model does not support image'. The 'exp' suffix means the vision model is still experimental, so pin it in configuration rather than hardcoding it across your codebase.",
  },
  {
    q: "How do I send an image to the DeepSeek API?",
    a: "There are three ways, all through the OpenAI-compatible Chat Completions endpoint at https://api.deepseek.com: a base64 data: URL inline in an image_url block, a publicly reachable http(s) link in an image_url block, or a file block that references a file_id returned by the Files API. In each case the message content becomes an array of blocks instead of a plain string.",
  },
  {
    q: "How many tokens does an image cost on DeepSeek?",
    a: "Images are billed as tokens based on their dimensions after an automatic resize. Anything under roughly 384×384 pixels is scaled up preserving aspect ratio; anything larger is scaled down to roughly the pixel count of an 800×800 image. The result is a hard ceiling of 384 tokens per image, so a 2000×2000 image and a 5000×5000 image cost exactly the same. Each image in a multi-image request is counted independently under the same rule.",
  },
  {
    q: "When should I use the Files API instead of base64?",
    a: "Use the Files API when a single request would exceed the 48 MiB body limit, when one image is larger than 32 MiB (only possible via Files API, which allows up to 64 MiB), or when you reference the same image across multiple requests and want to avoid re-uploading it each time.",
  },
  {
    q: "Can I put an image in a system or assistant message?",
    a: "No. Images are supported in user messages only. An image inside a system or assistant message returns a 400 error. User text containing the reserved image placeholder token is also rejected with a 400.",
  },
  {
    q: "Does DeepSeek vision work through the Anthropic-compatible endpoint?",
    a: "Yes. Point base_url at https://api.deepseek.com/anthropic and use the Anthropic /messages shape: instead of image_url, send an image block with a source object whose type is base64, url or file. The three source variants mirror the three OpenAI methods.",
  },
  {
    q: "What can DeepSeek vision actually be used for?",
    a: "Practical, verified use cases from the documentation: describing photographs, reading text out of screenshots, and analyzing charts and diagrams. Because it accepts up to 600 images per request, batch document and screenshot pipelines are the strongest fit — paired with the low detail level when you only need coarse recognition.",
  },
  {
    q: "How much does DeepSeek image input cost?",
    a: "Image tokens are billed together with your text tokens at the model's normal rate, so cost follows the V4-Flash rate card. With the 384-token ceiling per image, a thousand images adds at most ~384K input tokens — cheap compared to most vision APIs. Check our pricing page for the live per-million rate before you budget.",
  },
];

const DeepseekVision = () => {
  const navigate = useNavigate();

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: "DeepSeek Vision API: Image Input with deepseek-v4-flash-vision-exp",
    datePublished: PUBLISHED,
    dateModified: PUBLISHED,
    author: {
      "@type": "Organization",
      name: "The Independent DeepSeek Guide",
      url: "https://deepseek.ai",
    },
    publisher: {
      "@type": "Organization",
      name: "The Independent DeepSeek Guide",
      logo: { "@type": "ImageObject", url: "https://deepseek.ai/og-image.png" },
    },
    description:
      "How to send images to DeepSeek: the three input methods, detail levels, the 384-token-per-image ceiling, every documented limit and the Anthropic-compatible variant.",
    image: ["https://deepseek.ai/og-image.png"],
    mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://deepseek.ai/" },
      { "@type": "ListItem", position: 2, name: "Blog", item: "https://deepseek.ai/blog" },
      { "@type": "ListItem", position: 3, name: "DeepSeek vision API", item: canonical },
    ],
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "Send an image to the DeepSeek API",
    description:
      "Send an image to deepseek-v4-flash-vision-exp using the OpenAI-compatible Chat Completions endpoint.",
    step: [
      { "@type": "HowToStep", name: "Point the client at DeepSeek", text: "Create an OpenAI client with base_url https://api.deepseek.com and your DeepSeek API key." },
      { "@type": "HowToStep", name: "Choose the vision model", text: "Set model to deepseek-v4-flash-vision-exp. No other DeepSeek model accepts images." },
      { "@type": "HowToStep", name: "Build a content array", text: "Replace the plain string content with an array containing a text block and an image_url block." },
      { "@type": "HowToStep", name: "Supply the image", text: "Use a base64 data URL, a public http(s) link, or a file block referencing a Files API file_id." },
      { "@type": "HowToStep", name: "Tune the detail level", text: "Set detail to low to downscale to 512×512 for cheaper, faster inference when fine detail is unnecessary." },
    ],
  };

  return (
    <>
      <Head>
        <title>DeepSeek Vision API Guide 2026 — Image Input</title>
        <meta
          name="description"
          content="DeepSeek now accepts images via deepseek-v4-flash-vision-exp. The three ways to send an image, detail levels, the 384-token-per-image cap, all documented limits and Python examples."
        />
        <link rel="canonical" href={canonical} />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        <meta property="og:title" content="DeepSeek Vision API: Image Input Explained" />
        <meta
          property="og:description"
          content="Base64, external URL or Files API — how to send images to DeepSeek's vision model, what each image costs in tokens, and every limit that will break your pipeline."
        />
        <meta property="og:image" content="https://deepseek.ai/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="DeepSeek Vision API Guide 2026 — Image Input" />
        <meta
          name="twitter:description"
          content="DeepSeek vision is here: deepseek-v4-flash-vision-exp, three image input methods, 384 tokens per image max, 600 images per request."
        />
        <meta name="twitter:image" content="https://deepseek.ai/og-image.png" />
        <meta property="article:published_time" content={PUBLISHED} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(howToSchema)}</script>
      </Head>

      <div className="min-h-screen bg-white">
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Button
            variant="ghost"
            onClick={() => navigate("/blog")}
            className="mb-6 text-slate-600 hover:text-[#0066FF]"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Blog
          </Button>

          <nav aria-label="Breadcrumb" className="text-xs text-slate-500 mb-4">
            <Link to="/" className="hover:text-[#0066FF]">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/blog" className="hover:text-[#0066FF]">Blog</Link>
            <span className="mx-2">/</span>
            <span className="text-slate-700">DeepSeek vision API</span>
          </nav>

          <div className="mb-6 inline-flex items-center gap-2 text-xs font-medium bg-blue-50 text-[#0066FF] border border-blue-200 px-3 py-1.5 rounded-full">
            <Eye className="h-3.5 w-3.5" />
            API guide · August 23, 2026 · Independent Guide
          </div>

          <article>
            <h1 className="text-3xl md:text-5xl font-bold text-slate-900 mb-4 leading-tight">
              DeepSeek Vision API: Sending Images to deepseek-v4-flash-vision-exp
            </h1>

            <p className="text-lg text-slate-600 mb-2">
              By the Deep Seek AI editorial desk · August 23, 2026 · 8 min read
            </p>
            <LastUpdated date="2026-08-23" className="mb-6" />

            <p className="text-base text-slate-500 mb-8 italic">
              <strong>In short:</strong> DeepSeek's vision model{" "}
              <code>deepseek-v4-flash-vision-exp</code> accepts images alongside text through the
              same OpenAI-compatible Chat Completions endpoint you already use. Three input methods,
              a hard ceiling of <strong>384 tokens per image</strong>, and up to{" "}
              <strong>600 images per request</strong>.
            </p>

            <Card className="mb-8 border-amber-200 bg-amber-50/70">
              <CardContent className="p-6">
                <h2 className="text-lg font-bold text-slate-900 mb-2 mt-0 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  One model only — and it is experimental
                </h2>
                <p className="text-sm text-slate-800">
                  Images work exclusively with <code>deepseek-v4-flash-vision-exp</code>. Any other
                  DeepSeek model returns a <code>400</code> with "This model does not support
                  image". The <code>-exp</code> suffix signals experimental status, so keep the model
                  string in configuration, not scattered through your code.
                </p>
              </CardContent>
            </Card>

            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
              What DeepSeek vision can do
            </h2>
            <p className="text-slate-700 mb-4">
              The vision model takes images in the same message as your text prompt, which makes the
              obvious workloads immediately available: describing photographs, reading text out of
              screenshots, and analysing charts and diagrams. Image format is detected from the
              actual file content — JPEG, PNG, GIF and WebP — not from the extension or the declared
              MIME type, so a mislabelled upload still works as long as the bytes are valid.
            </p>
            <p className="text-slate-700 mb-6">
              The interesting part for anyone building a pipeline is the batch ceiling: a single
              request may carry up to <strong>600 images</strong>. Combined with the flat token cap
              per image, document triage and screenshot classification become genuinely cheap
              rather than merely possible.
            </p>

            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
              The three ways to send an image
            </h2>
            <p className="text-slate-700 mb-4">
              All three use the standard OpenAI-compatible Chat Completions format, where{" "}
              <code>content</code> is an array of blocks instead of a plain string. The same three
              methods exist in the Responses API, where images travel in{" "}
              <code>input_image</code> content parts. Base URL for everything below:{" "}
              <code>https://api.deepseek.com</code>.
            </p>

            <h3 className="text-xl font-bold text-slate-900 mt-8 mb-3">1. Base64 inline (local files)</h3>
            <p className="text-slate-700 mb-4">
              Encode the file and embed it as a <code>data:</code> URL. Simplest option for local
              images; the encoded bytes count toward the 48 MiB request body limit.
            </p>
            <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto text-sm mb-6">
              <code>{`import base64
from openai import OpenAI

client = OpenAI(api_key="<DeepSeek API Key>", base_url="https://api.deepseek.com")

with open("image.jpg", "rb") as f:
    b64 = base64.b64encode(f.read()).decode("utf-8")

response = client.chat.completions.create(
    model="deepseek-v4-flash-vision-exp",
    messages=[
        {
            "role": "user",
            "content": [
                {"type": "text", "text": "What is in this image?"},
                {
                    "type": "image_url",
                    "image_url": {"url": f"data:image/jpeg;base64,{b64}"},
                },
            ],
        }
    ],
)
print(response.choices[0].message.content)`}</code>
            </pre>

            <h3 className="text-xl font-bold text-slate-900 mt-8 mb-3">2. External image URL</h3>
            <p className="text-slate-700 mb-4">
              Pass a publicly reachable <code>http(s)</code> link and DeepSeek downloads the image
              for you. The URL must be at most 8,192 characters, the file at most 32 MiB, and the
              download must finish inside 60 seconds — otherwise fall back to base64 or the Files
              API.
            </p>
            <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto text-sm mb-6">
              <code>{`response = client.chat.completions.create(
    model="deepseek-v4-flash-vision-exp",
    messages=[
        {
            "role": "user",
            "content": [
                {"type": "text", "text": "Describe this image."},
                {
                    "type": "image_url",
                    "image_url": {"url": "https://example.com/image.jpg"},
                },
            ],
        }
    ],
)`}</code>
            </pre>

            <h3 className="text-xl font-bold text-slate-900 mt-8 mb-3">3. Files API reference</h3>
            <p className="text-slate-700 mb-4">
              Upload once, reference the returned <code>file_id</code> (shaped{" "}
              <code>file-api-…</code>) as often as you like. This is the right choice for reused
              images, and the only route for images above 32 MiB — Files API images may reach 64
              MiB and skip the per-image inline check.
            </p>
            <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto text-sm mb-4">
              <code>{`response = client.chat.completions.create(
    model="deepseek-v4-flash-vision-exp",
    messages=[
        {
            "role": "user",
            "content": [
                {"type": "text", "text": "What is in this image?"},
                {"type": "file", "file_id": "file-api-xxxxxxxxxxxxxxxx"},
            ],
        }
    ],
)`}</code>
            </pre>
            <p className="text-slate-700 mb-6">
              A <code>file</code> block can alternatively carry the image inline as base64 through{" "}
              <code>file_data</code> plus <code>filename</code>. <code>file_data</code> and{" "}
              <code>file_id</code> are mutually exclusive.
            </p>

            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
              Detail levels: the cheapest knob you have
            </h2>
            <p className="text-slate-700 mb-4">
              For <code>image_url</code> inputs you can set an optional <code>detail</code> field
              that controls how the image is processed before inference.
            </p>
            <div className="overflow-x-auto mb-6">
              <table className="w-full text-sm border border-slate-200 rounded-lg">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-3 font-semibold text-slate-900">Value</th>
                    <th className="text-left p-3 font-semibold text-slate-900">Behaviour</th>
                  </tr>
                </thead>
                <tbody>
                  {detailLevels.map((d) => (
                    <tr key={d.value} className="border-t border-slate-200 align-top">
                      <td className="p-3 font-medium text-slate-800 whitespace-nowrap"><code>{d.value}</code></td>
                      <td className="p-3 text-slate-700">{d.behavior}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-slate-700 mb-6">
              If your task is coarse recognition — "is this a receipt or an invoice?" — set{" "}
              <code>detail: "low"</code> and let DeepSeek downscale to 512×512. Keep{" "}
              <code>original</code> for small print, dense tables and OCR-style work.
            </p>

            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
              Token usage: 384 tokens per image, maximum
            </h2>
            <p className="text-slate-700 mb-4">
              Images become tokens based on their dimensions, billed together with your text tokens.
              Before inference every image is resized automatically: below roughly 384×384 total
              pixels it is scaled <em>up</em> preserving aspect ratio; larger images are scaled{" "}
              <em>down</em> preserving aspect ratio to roughly the pixel count of an 800×800 image.
            </p>
            <p className="text-slate-700 mb-6">
              The practical consequence is an upper bound of <strong>384 tokens per image</strong>.
              A 2000×2000 photo and a 5000×5000 scan cost the same, so there is no billing reason to
              downscale before upload — only a bandwidth one. In multi-image requests each image is
              counted independently under exactly the same rule; there is no separate multi-image
              formula.
            </p>

            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
              Every documented limit
            </h2>
            <div className="overflow-x-auto mb-8">
              <table className="w-full text-sm border border-slate-200 rounded-lg">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-3 font-semibold text-slate-900">Limit</th>
                    <th className="text-left p-3 font-semibold text-slate-900">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {limits.map((l) => (
                    <tr key={l.limit} className="border-t border-slate-200 align-top">
                      <td className="p-3 font-medium text-slate-800">{l.limit}</td>
                      <td className="p-3 text-slate-700">{l.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
              Restrictions that will bite you
            </h2>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-8">
              <li>
                <strong>User messages only.</strong> An image inside a <code>system</code> or{" "}
                <code>assistant</code> message returns a <code>400</code>. If you replay
                conversation history containing assistant-side images, strip them.
              </li>
              <li>
                <strong>Vision model only.</strong> Non-vision models reject images with a{" "}
                <code>400</code> and "This model does not support image".
              </li>
              <li>
                <strong>Reserved placeholder token.</strong> User text containing the reserved image
                placeholder token is rejected with a <code>400</code>.
              </li>
              <li>
                <strong>Dimension cliff at 15 images.</strong> The 8,192 px per-side maximum drops
                to 4,096 px per side as soon as a request contains 15 or more images — a
                surprisingly easy way to break a working batch job by adding one more page.
              </li>
            </ul>

            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
              Using images through the Anthropic-compatible endpoint
            </h2>
            <p className="text-slate-700 mb-4">
              If your stack already speaks Anthropic, point <code>base_url</code> at{" "}
              <code>https://api.deepseek.com/anthropic</code> and use the <code>/messages</code>{" "}
              shape. The only difference is the image block: instead of <code>image_url</code>,
              Anthropic uses an <code>image</code> block with a <code>source</code> object whose{" "}
              <code>type</code> is <code>base64</code>, <code>url</code> or <code>file</code> — the
              three variants mirroring the OpenAI methods above.
            </p>
            <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto text-sm mb-8">
              <code>{`import anthropic

# ANTHROPIC_BASE_URL=https://api.deepseek.com/anthropic
client = anthropic.Anthropic()

message = client.messages.create(
    model="deepseek-v4-flash-vision-exp",
    max_tokens=1024,
    messages=[
        {
            "role": "user",
            "content": [
                {"type": "text", "text": "What is in this image?"},
                {
                    "type": "image",
                    "source": {
                        "type": "base64",
                        "media_type": "image/jpeg",
                        "data": "<BASE64_DATA>",
                    },
                },
            ],
        }
    ],
)
print(message.content)`}</code>
            </pre>

            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
              A sane default configuration
            </h2>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-8">
              <li>
                <strong>Batch, but stay under 15 images</strong> per request unless you are certain
                every page is within 4,096 px per side.
              </li>
              <li>
                <strong>Prefer the Files API for anything reused</strong> — the same invoice
                template re-uploaded 500 times is pure wasted bandwidth.
              </li>
              <li>
                <strong>Default to <code>detail: "low"</code></strong> for classification and
                routing; escalate to <code>original</code> only on the pages that need reading.
              </li>
              <li>
                <strong>Budget 384 input tokens per image</strong> as a worst case — it is the
                actual ceiling, so your estimate can never be short.
              </li>
            </ul>

            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
              Frequently asked questions
            </h2>
            <Accordion type="single" collapsible className="mb-10">
              {faqs.map((f, i) => (
                <AccordionItem key={f.q} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left text-slate-900 font-semibold">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-700">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <Card className="mb-10 border-blue-200 bg-blue-50/60">
              <CardContent className="p-6">
                <h2 className="text-lg font-bold text-slate-900 mb-2 mt-0 flex items-center gap-2">
                  <Search className="h-4 w-4 text-[#0066FF]" />
                  Keep reading
                </h2>
                <ul className="text-sm space-y-2 list-disc pl-5 text-slate-800">
                  <li>
                    <Link to="/deepseek-api" className="text-[#0066FF] underline">
                      The DeepSeek API explained
                    </Link>
                  </li>
                  <li>
                    <Link to="/blog/deepseek-v4-flash-ga-agent-benchmarks" className="text-[#0066FF] underline">
                      DeepSeek V4-Flash: public beta and agent benchmarks
                    </Link>
                  </li>
                  <li>
                    <Link to="/blog/deepseek-ocr-context-compression" className="text-[#0066FF] underline">
                      DeepSeek OCR and context compression
                    </Link>
                  </li>
                  <li>
                    <Link to="/pricing" className="text-[#0066FF] underline">
                      Current DeepSeek pricing and cost calculator
                    </Link>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <p className="text-xs text-slate-500 mb-8">
              This is an independent, fan-run guide. It is not affiliated with, endorsed by or
              operated by DeepSeek. Values above are taken from DeepSeek's official vision
              documentation on the date shown — verify against the official docs before shipping.
            </p>

            <RelatedLinks />
            <NavigationButtons />
          </article>
        </main>
      </div>
    </>
  );
};

export default DeepseekVision;
